import { useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useProductTypes } from "@/lib/productTypes";

export const ProductFilters = ({ filters, setFilter, options, displayCurrency = "USD" }) => {
  const { t, lang } = useLanguage();
  const productTypes = useProductTypes();  // dynamic 2026-07

  // 2026-07 · Selling-price filter follows the products page currency toggle
  // (US$ / C$) — the label switches and the backend receives ``price_currency``
  // so it inverts the correct display-price formula.
  const priceCurrency = displayCurrency === "CAD" ? "CAD" : "USD";
  useEffect(() => {
    if (filters.price_currency !== priceCurrency) {
      setFilter("price_currency", priceCurrency);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceCurrency]);

  // Subcategory dropdown cascades off the currently-selected category (per user spec
  // 2026-02): with a category chosen we only show its children; without a category
  // we show every subcategory in the catalog so buyers can still browse cross-category.
  const subEntries = options.subcategories || [];
  const filteredSubEntries = filters.category
    ? subEntries.filter((sc) => sc.category === filters.category)
    : subEntries;
  // De-dup by name (a subcategory could theoretically live under two categories).
  const subOptions = [...new Set(filteredSubEntries.map((sc) => sc.name).filter(Boolean))].sort();

  const handleCategoryChange = (v) => {
    setFilter("category", v);
    // Clear a stale subcategory choice if it no longer belongs to the new category.
    if (v !== "__all__" && filters.subcategory) {
      const stillValid = subEntries.some((sc) => sc.name === filters.subcategory && sc.category === v);
      if (!stillValid) setFilter("subcategory", "");
    }
  };

  // 2026-06 · 不再自己包一层 flex-wrap —— 直接渲染成 fragment，让筛选项与父级
  // 工具栏（显示选择/显示全部/币种/视图）共用同一行折行流，整体最多两行。
  return (
    <>
      {/* 产品类型 — 2026-02 spec: moved to leftmost position so users can
          quickly narrow the catalog before drilling by category/brand. */}
      <Select value={filters.product_type || "__all__"} onValueChange={(v) => setFilter("product_type", v === "__all__" ? "" : v)}>
        <SelectTrigger data-testid="filter-product-type-select" className={`w-auto min-w-[104px] rounded-md h-9 text-sm ${filters.product_type ? "border-brand text-brand" : ""}`}>
          <span className="text-muted-foreground mr-1">{t("products.productType")}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{t("common.all")}</SelectItem>
          {productTypes.map((pt) => (
            <SelectItem key={pt.code} value={pt.code}>
              {(lang === "en" ? pt.name_en : pt.name_zh) || pt.code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={filters.category || "__all__"} onValueChange={handleCategoryChange}>
        <SelectTrigger data-testid="filter-category-select" className={`w-auto min-w-[104px] rounded-md h-9 text-sm ${filters.category ? "border-brand text-brand" : ""}`}>
          <span className="text-muted-foreground mr-1">{t("products.category")}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{t("common.all")}</SelectItem>
          {(options.categories || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.subcategory || "__all__"} onValueChange={(v) => setFilter("subcategory", v)}>
        <SelectTrigger
          data-testid="filter-subcategory-select"
          className={`w-auto min-w-[104px] rounded-md h-9 text-sm ${filters.subcategory ? "border-brand text-brand" : ""}`}
          disabled={subOptions.length === 0}
        >
          <span className="text-muted-foreground mr-1">{t("products.subcategory")}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{t("common.all")}</SelectItem>
          {subOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.brand || "__all__"} onValueChange={(v) => setFilter("brand", v)}>
        <SelectTrigger data-testid="filter-brand-select" className={`w-auto min-w-[104px] rounded-md h-9 text-sm ${filters.brand ? "border-brand text-brand" : ""}`}>
          <span className="text-muted-foreground mr-1">{t("products.brand")}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{t("common.all")}</SelectItem>
          {(options.brands || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
      {/* Creation-date range filter — 2026-07 restored per user request, but
          no preset start date (default_from is empty so users see the full
          catalog until they explicitly narrow it). */}
      <div className="flex items-center gap-1 shrink-0" title={t("products.creationDateFilter")}>
        <span className="text-sm text-muted-foreground">{t("products.creationDateFilter")}:</span>
        <Input
          type="date"
          value={filters.date_from || ""}
          onChange={(e) => setFilter("date_from", e.target.value)}
          className={`h-9 w-[136px] rounded-md font-mono text-xs ${filters.date_from ? "border-brand text-brand" : ""}`}
          data-testid="filter-date-from-input"
        />
        <span className="text-muted-foreground text-xs">–</span>
        <Input
          type="date"
          value={filters.date_to || ""}
          onChange={(e) => setFilter("date_to", e.target.value)}
          className={`h-9 w-[136px] rounded-md font-mono text-xs ${filters.date_to ? "border-brand text-brand" : ""}`}
          data-testid="filter-date-to-input"
        />
      </div>
      {/* 2026-07 · Factory-cost range — currency toggle (RMB/USD) sits inline
          with the two inputs so users can pick either scale. */}
      <div className="flex items-center gap-1 shrink-0" title={t("products.factoryCostFilter")}>
        <span className="text-sm text-muted-foreground">{t("products.factoryCostFilter")}:</span>
        <Select value={filters.cost_currency || "RMB"} onValueChange={(v) => setFilter("cost_currency", v)}>
          <SelectTrigger data-testid="filter-cost-currency-select" className="h-9 w-[70px] rounded-md text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="RMB">RMB</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder={t("products.min")}
          value={filters.cost_min || ""}
          onChange={(e) => setFilter("cost_min", e.target.value)}
          className={`h-9 w-[72px] rounded-md text-xs ${filters.cost_min ? "border-brand text-brand" : ""}`}
          data-testid="filter-cost-min-input"
        />
        <span className="text-muted-foreground text-xs">–</span>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder={t("products.max")}
          value={filters.cost_max || ""}
          onChange={(e) => setFilter("cost_max", e.target.value)}
          className={`h-9 w-[72px] rounded-md text-xs ${filters.cost_max ? "border-brand text-brand" : ""}`}
          data-testid="filter-cost-max-input"
        />
      </div>
      {/* 2026-07 · Selling-price range (USD reference price). Backend inverts
          the display-price formula to a factory_cost_usd range under the
          global USD margin so the filter runs on a single index lookup. */}
      <div className="flex items-center gap-1 shrink-0" title={t("products.sellingPriceFilter")}>
        <span className="text-sm text-muted-foreground">{t("products.sellingPriceFilter")}:</span>
        <span className="text-xs text-muted-foreground" data-testid="filter-price-currency-label">{priceCurrency === "CAD" ? "C$" : "US$"}</span>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder={t("products.min")}
          value={filters.price_min || ""}
          onChange={(e) => setFilter("price_min", e.target.value)}
          className={`h-9 w-[72px] rounded-md text-xs ${filters.price_min ? "border-brand text-brand" : ""}`}
          data-testid="filter-price-min-input"
        />
        <span className="text-muted-foreground text-xs">–</span>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder={t("products.max")}
          value={filters.price_max || ""}
          onChange={(e) => setFilter("price_max", e.target.value)}
          className={`h-9 w-[72px] rounded-md text-xs ${filters.price_max ? "border-brand text-brand" : ""}`}
          data-testid="filter-price-max-input"
        />
      </div>
    </>
  );
};
